import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { QueryKey } from '@/Config/api/queryKey';

export const ONBOARDING_DISCOUNT_PERCENT_OFF = 50;

export interface OnboardingDiscountState {
  code: string;
  expires_at: string; // ISO 8601
}

export function useOnboardingDiscount(): {
  state: OnboardingDiscountState | null;
  remainingMs: number;
  isClaimable: boolean;
} {
  const { data } = useQuery<OnboardingDiscountState | null>({
    queryKey: [QueryKey.gettingStarted.discount],
  });

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!data) return { state: null, remainingMs: 0, isClaimable: false };
  const expiresAtMs = new Date(data.expires_at).getTime();
  const remainingMs = Math.max(0, expiresAtMs - Date.now());
  return { state: data, remainingMs, isClaimable: remainingMs > 0 };
}

export function useIssueOnboardingDiscount() {
  const queryClient = useQueryClient();
  // POST /discount returns 200 with the code or 204 (e.g. the user is no longer
  // eligible because they upgraded in another tab). The mutationFn maps 204 to
  // an empty body via axios defaults, so consumers must guard on `data?.code`.
  return useMutation<OnboardingDiscountState | null>({
    mutationKey: [QueryKey.gettingStarted.discount],
    onSuccess: (data) => {
      queryClient.setQueryData([QueryKey.gettingStarted.discount], data ?? null);
    },
  });
}

export function formatDiscountRemaining(ms: number): string {
  const safe = Math.max(0, ms);
  const hours = Math.floor(safe / 3600000);
  const minutes = Math.floor((safe % 3600000) / 60000);
  const seconds = Math.floor((safe % 60000) / 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}
